import EVImg from '@components/EVImg/EVImg';
import {
  InputField,
  InputFieldAdornment,
  InputPaymentGroup,
  SelectInput,
} from '@components/FormFields';
import { Block } from '@components/Layout';
import { convertCurrencyToNumber } from '@util/convertCurrencyToNumber';
import { useCurrency } from '@util/customHooks/useCurrency';
import useIsMobile from '@util/customHooks/useIsMobile';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Markdown from 'markdown-to-jsx';
import { OSNComplianceData } from 'mobx/stores/app-store';
import { useFormatter, useTranslations } from 'next-intl';
import { FC, useEffect, useState } from 'react';
import {
  CustomDisclaimer,
  Header,
  List,
  ListCol,
  ListColAlt,
  PaymentCalculatorStyles,
  StyledLogoWrapper,
  Text,
} from './styles';

dayjs.extend(utc);
dayjs.extend(timezone);

interface PaymentCalculatorProps {
  currencyCode: string;
  defaultValue?: number;
  disclaimer?: string;
  mlsComplianceObj?: OSNComplianceData;
  modificationTimeStamp?: string;
}

const PaymentCalculator: FC<PaymentCalculatorProps> = ({
  defaultValue,
  mlsComplianceObj,
  disclaimer,
  currencyCode,
  modificationTimeStamp,
}) => {
  const t = useTranslations();
  const format = useFormatter();
  const { formatCurrency } = useCurrency();
  const timezone = dayjs.tz.guess();
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [term, setTerm] = useState<number>(30);
  const [rate, setRate] = useState<number>(5.75);
  const [homePrice, setHomePrice] = useState(convertCurrencyToNumber(defaultValue));
  const [downPayment, setDownPayment] = useState(0);
  const isMobile = useIsMobile();

  const handleInputChange = (event, func) => {
    let val = parseFloat(event.target.value);
    if (isNaN(val)) val = 0;
    func(val);
  };
  const handleSelectChange = (event, func) => {
    func(event.target.value);
  };

  const handleDownPaymentChange = (event) => {
    let val = convertCurrencyToNumber(event.target.value);
    if (isNaN(val)) val = 0;
    if (val > homePrice) val = homePrice;
    setDownPayment(val);
  };

  const handleDownPaymentPercChange = (event) => {
    let val = parseFloat(event.target.value);
    if (isNaN(val)) val = 0;
    if (val >= 100) val = 100;
    let amt = (val / 100) * homePrice;
    setDownPayment(amt);
  };
  const calcMonthlyPayment = () => {
    let loanAmt = homePrice - downPayment;
    let payment =
      (loanAmt * ((rate / 12 / 100) * (1 + rate / 12 / 100) ** (term * 12))) /
      ((1 + rate / 12 / 100) ** (term * 12) - 1);
    // round to 2 digits
    payment = Math.round(payment * 100) / 100;
    setMonthlyPayment(payment);
  };

  useEffect(() => {
    calcMonthlyPayment();
  }, [homePrice, downPayment, term, rate, monthlyPayment]);

  useEffect(() => {
    if (defaultValue) {
      setHomePrice(convertCurrencyToNumber(defaultValue));
    } else {
      setHomePrice(0);
    }
  }, [defaultValue]);

  const currentTimeRoundedToTenMinutes = new Date().setMinutes(
    new Date().getMinutes() - (new Date().getMinutes() % 10),
  );

  return (
    <>
      <Block narrow>
        <List $gap="x7">
          <List>
            <Header data-testid="calculator-header" $mobileFont="headline-xxs">
              {t('Payment Calculator')}
            </Header>
            <Text data-testid="calculator-description" $mobileFont="body-sm-light">
              {t('Enter your payment information to receive an estimated monthly payment')}
            </Text>
          </List>
          <ListColAlt $gap="x7">
            <List>
              <Text data-testid="home-price-text" $mobileFont="body-sm">
                {t('Home Price')}
              </Text>
              <InputFieldAdornment $position="start" $disabled>
                <InputField
                  disabled
                  data-testid="home-price-value"
                  placeholder={t('Home Price')}
                  value={formatCurrency(homePrice, currencyCode, 'symbol')}
                  onChange={(event) => handleInputChange(event, setHomePrice)}
                />
              </InputFieldAdornment>

              <Text data-testid="down-payment-text" $mobileFont="body-sm" $m="20px 0 0">
                {t('Down Payment')}
              </Text>
              <InputPaymentGroup>
                <InputField
                  data-testid="down-payment-value"
                  placeholder="Down payment"
                  value={formatCurrency(downPayment, currencyCode, 'symbol')}
                  onChange={handleDownPaymentChange}
                />
                <InputField
                  data-testid="down-payment-percent"
                  placeholder="%"
                  value={Math.round((downPayment / homePrice) * 100 * 100) / 100}
                  onChange={handleDownPaymentPercChange}
                />
              </InputPaymentGroup>

              <Text data-testid="mortgage-text" $mobileFont="body-sm" $m="20px 0 0">
                {t('Mortgage Loan')}
              </Text>
              <ListCol data-testid="mortgage-loan-options" $gap="lg" $centerVert>
                <SelectInput
                  data-testid="mortgage-length-options"
                  value={term}
                  onChange={(event) => handleSelectChange(event, setTerm)}
                >
                  <option data-testid="option-30" value="30">
                    30
                  </option>
                  <option data-testid="option-25" value="25">
                    25
                  </option>
                  <option data-testid="option-20" value="20">
                    20
                  </option>
                  <option data-testid="option-15" value="15">
                    15
                  </option>
                  <option data-testid="option-10" value="10">
                    10
                  </option>
                  <option data-testid="option-5" value="5">
                    5
                  </option>
                </SelectInput>
                <Text
                  $mobileFont="body-sm"
                  data-testid="year-fixed-text"
                  style={{
                    paddingBottom: '0px',
                    fontWeight: 300,
                    lineHeight: 1.1,
                  }}
                >
                  {t('Year Fixed')}
                </Text>
                <SelectInput
                  data-testid="interest-rate-options"
                  value={rate}
                  onChange={(event) => handleSelectChange(event, setRate)}
                >
                  <option data-testid="interest-option-1" value={5.75}>
                    5.75%
                  </option>
                  <option data-testid="interest-option-2" value={7.5}>
                    7.5%
                  </option>
                </SelectInput>
              </ListCol>
            </List>
            <List $p={isMobile ? 'x7' : 'x10'} $bg="#C5DBE980" $center>
              <PaymentCalculatorStyles>
                <Text data-testid="monthly-payment-text" $mobileFont="body-sm">
                  {t('Your Monthly Payment')}
                </Text>
                <Header
                  data-testid="monthly-payment-amount"
                  $font="headline-xl"
                  $mobileFont="headline-lg"
                  $color="#4B6B90"
                  $m="0px"
                >
                  {formatCurrency(monthlyPayment, currencyCode, 'symbol')}
                </Header>
              </PaymentCalculatorStyles>
            </List>
          </ListColAlt>
          <List>
            {disclaimer && (
              <CustomDisclaimer
                data-testid="calculator-disclaimer"
                $font="body-xs-light"
                $color="#757575"
              >
                <Markdown data-testid>{disclaimer}</Markdown>
              </CustomDisclaimer>
            )}
            {mlsComplianceObj?.disclaimer && (
              <CustomDisclaimer
                data-testid="mls-compliance-disclaimer"
                $font="body-xs-light"
                $color="#757575"
              >
                <Markdown>{mlsComplianceObj.disclaimer}</Markdown>
              </CustomDisclaimer>
            )}
            {mlsComplianceObj?.img?.fields.file?.url && (
              <StyledLogoWrapper>
                <EVImg
                  src={mlsComplianceObj?.img?.fields.file?.url}
                  dataTestId="disclaimer-ev-logo"
                  imageDesc={mlsComplianceObj.name}
                  priority={true}
                  layout="fill"
                  objectFit="contain"
                  unoptimized={true}
                />
              </StyledLogoWrapper>
            )}
            {modificationTimeStamp && (
              <CustomDisclaimer
                data-testid="disclaimer-date"
                $font="body-xs-light"
                $color="#757575"
              >
                <p>
                  {t('Updated')}:{' '}
                  {dayjs
                    .tz(currentTimeRoundedToTenMinutes, timezone)
                    .format('MMMM D, YYYY  h:mm A')}
                </p>
              </CustomDisclaimer>
            )}
          </List>
        </List>
      </Block>
    </>
  );
};

export default PaymentCalculator;
